import React, { useState, useEffect, useMemo, useLayoutEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import ReactEcharts from 'echarts-for-react';
import merge from 'lodash.merge';

import { Div } from '@pinpoint/design-kit';

import Wrapper from 'src/Wrapper';
import { traitsPropType, stylePropType } from 'src/PropTypes';
import { Style1 } from 'src/styles';
import { Palette } from 'src/components';

import * as themes from 'src/themes';

const FACTOR = 5;

function Tornado({
  title,
  labels,
  series,
  style,
  options,
  formatter,
  onMouseover,
  theme: themeProp,
  oceanStyle,
  relative,
  onThemeChange,
  onOceanStyleChange,
  width,
  height,
  defaultTheme,
}) {
  const [theme, setTheme] = useState(themeProp);
  const [themeJson, setThemeJson] = useState(themes[themeProp] ? themes[themeProp] : themes.pinpoint);
  const [isOceanStyle, setOceanStyle] = useState(false);

  useEffect(() => {
    setOceanStyle(oceanStyle);
  }, [oceanStyle]);

  useEffect(() => {
    setTheme(themeProp);
    setThemeJson(themes[themeProp] ? themes[themeProp] : themes.pinpoint);
  }, [themeProp]);

  const minAxisValue = 0;
  let maxAxisValue = 50;
  if (relative) {
    const maxValue = series.reduce((currentMax, { values }) => {
      const localMax = Math.max(...values.map((value) => Math.abs(50 - value)));
      return localMax > currentMax ? localMax : currentMax;
    }, 0);
    maxAxisValue = maxValue % FACTOR === 0 ? maxValue : Math.floor(maxValue / FACTOR) * FACTOR + FACTOR;
  }
  const seriesList = useMemo(() => {
    const output = [];
    for (let i = 0; i < series.length; i += 1) {
      output.push({
        type: 'bar',
        name: series[i].name,
        xAxisIndex: 2,
        yAxisIndex: 2,
        data: series[i].values.map((value, index) => {
          const relativeValue = value - 50;
          if (relativeValue < 0) return null;
          const returnObject = {
            ...style.Tornado.right.series,
            label: {
              show: true,
              formatter,
              ...style.Tornado.right.series.label,
              ...options.label,
            },
            value: relativeValue,
            itemStyle: {
              color: isOceanStyle ? labels[index].oceanColor || '#A0A0A0' : themeJson.color[i],
              ...style.Tornado.right.series.itemStyle,
            },
          };
          if (isOceanStyle) {
            returnObject.tooltip = {
              formatter: labels[index].factor.toUpperCase(),
            };
          }
          return returnObject;
        }),
      });
      output.push({
        type: 'bar',
        name: 'fingerprint',
        data: series[i].values.map((value, index) => {
          const relativeValue = value - 50;
          if (relativeValue > 0) return null;
          const returnObject = {
            ...style.Tornado.left.series,
            label: {
              show: true,
              formatter,
              ...style.Tornado.left.series.label,
              ...options.label,
            },
            value: relativeValue * -1,
            itemStyle: {
              color: isOceanStyle ? labels[index].oceanColor || '#A0A0A0' : themeJson.color[i],
              ...style.Tornado.left.series.itemStyle,
            },
          };
          if (isOceanStyle) {
            returnObject.tooltip = {
              formatter: labels[index].factor.toUpperCase(),
            };
          }
          return returnObject;
        }),
      });
    }
    return output;
  }, [isOceanStyle, series, labels, themeJson]);

  const chartOption = {
    title: {
      text: title,
      ...style.global.title,
    },
    textStyle: {
      ...style.global.textStyle,
    },
    backgroundColor: style.global.backgroundColor,
    toolbox: {
      ...style.global.toolbox,
    },
    tooltip: {
      trigger: isOceanStyle ? 'item' : 'none',
    },
    grid: [
      {
        show: false,
        left: '12%',
        containLabel: false,
        width: '33%',
      },
      {
        show: false,
        left: '51%',
        containLabel: false,
        width: '10%',
      },
      {
        show: false,
        right: '12%',
        containLabel: false,
        width: '33%',
      },
    ],
    xAxis: [
      // left axis
      {
        min: minAxisValue,
        max: maxAxisValue,
        ...style.Tornado.left.xAxis,
      },
      {
        gridIndex: 1,
        ...style.Tornado.center.xAxis,
      },
      {
        min: minAxisValue,
        max: maxAxisValue,
        gridIndex: 2,
        ...style.Tornado.right.xAxis,
      },
    ],
    yAxis: [
      // left grid
      {
        ...style.Tornado.left.yAxis,
        data: labels.map(({ lowRangeName }) => ({
          ...style.Tornado.left.yAxis.data,
          value: lowRangeName,
        })),
      },
      {
        ...style.Tornado.center.yAxis,
        gridIndex: 1,
        data: labels.map(({ trait }) => ({
          ...style.Tornado.center.yAxis.data,
          value: trait,
        })),
      },
      {
        ...style.Tornado.right.yAxis,
        gridIndex: 2,
        data: labels.map(({ highRangeName }) => ({
          ...style.Tornado.right.yAxis.data,
          value: highRangeName,
        })),
      },
    ],
    series: seriesList,
  };

  const mergedOptions = merge(chartOption, options);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <Wrapper>
      <ReactEcharts
        ref={(element) => {
          const instance = element ? element.getEchartsInstance() : null;
          if (instance) {
            instance.on('mouseover', ({ dataIndex: index }) => {
              const label = labels[index];
              const values = series.map((_series) => _series.values[index]);
              onMouseover({ label, values });
            });
          }
        }}
        style={{
          width,
          height,
        }}
        option={mergedOptions}
        theme={theme}
        notMerge
      />
      <Div d="flex" h="2.5rem" justify="space-between">
        <Palette
          defaultTheme={defaultTheme}
          selected={theme}
          onSelect={(newTheme) => {
            setTheme(newTheme);
            setThemeJson(themes[newTheme]);
            setOceanStyle(false);
            onOceanStyleChange(false);
            onThemeChange(newTheme);
          }}
          style={{
            backgroundColor: mergedOptions.backgroundColor,
            width: '10rem',
            margin: '0 auto',
            paddingTop: '0.5rem',
          }}
        />
        <Div d="flex" justify="flex-end" m={{ l: '-2.5rem' }}>
          <Div
            tag="span"
            h="2.5rem"
            w="2.5rem"
            rounded="md"
            border="1px solid"
            borderColor="shadeE6"
            cursor="pointer"
            hoverBorderColor="shadeCC"
            data-tip="Add or remove OCEAN color coding"
            style={{
              ...style.COLORS.OCEAN_CSS,
            }}
            onClick={() => {
              setOceanStyle((currentStyle) => {
                onOceanStyleChange(!currentStyle);
                return !currentStyle;
              });
            }}
            m={{ l: '0.4rem' }}
          />
        </Div>
      </Div>
    </Wrapper>
  );
}

Tornado.propTypes = {
  title: PropTypes.string,
  labels: traitsPropType.isRequired,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      values: PropTypes.arrayOf(PropTypes.number),
    })
  ).isRequired,
  style: stylePropType,
  options: PropTypes.shape({}),
  formatter: PropTypes.func,
  onMouseover: PropTypes.func,
  theme: PropTypes.string,
  oceanStyle: PropTypes.bool,
  relative: PropTypes.bool,
  onThemeChange: PropTypes.func,
  onOceanStyleChange: PropTypes.func,
  width: PropTypes.string,
  height: PropTypes.string,
};

Tornado.defaultProps = {
  title: '',
  style: Style1,
  options: {},
  formatter: (params) => params.value.toFixed(1),
  onMouseover: () => null,
  theme: 'pinpoint',
  oceanStyle: false,
  relative: false,
  onThemeChange: () => null,
  onOceanStyleChange: () => null,
  width: '100%',
  height: 'calc(100% - 2.5rem)',
};

export default Tornado;
